import React, { useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { useWindowSize } from 'react-use';

import { useTranslation, i18n } from 'next-i18next';
import dynamic from 'next/dynamic';

import type { SxProps } from '@mui/material';

import Typography from 'src/components/ui/Typography';
import { colours } from 'src/constants/colours';
import Icon from 'src/components/ui/Icon';
import Link from 'src/components/ui/Link';
import Avatar from 'src/components/ui/Avatar';
import Loader from 'src/components/ui/Loader';
import StoriesModal from 'src/components/news/StoriesModal';
import Lightbox from 'src/components/ui/Lightbox';
import AvatarLightBox from 'src/components/ui/AvatarLightBox';
import GroupLabel from 'src/components/profile/GroupLabel';
import FollowModal from 'src/components/profile/follow/FollowModal';
import Image from 'src/components/ui/Image';
import Button from 'src/components/ui/Button';

import theme from 'src/utils/theme';

import { useCompanyActivityToDisplay } from 'src/hooks/useActivityToDisplay';
import useAppRouter from 'src/hooks/useAppRouter';
import useModalOpenState from 'src/hooks/useModalOpenState';

import { defaultWoopenImagePath } from 'src/constants/defaultImages';

import { paddingAroundCoverInPx, ratioCover } from 'src/constants/misc';
import { routePathCompany } from 'src/constants/router';
import { useCompanyProfileQuery } from 'src/hooks/__generated__/queries';
import useEvents from 'src/hooks/useEvents';
import useUser from 'src/hooks/useUser';
import type {
    Company,
    CompanyProfileQuery,
    Medium,
    Owner,
    PagesJaunesAddress,
    PagesJaunesPro,
    QuickSignupCompany,
} from 'src/types/__generated__/graphql';
import { getPagesJaunesImageUrl } from 'src/utils/pagesJaunesUtil';
import { styledComponent, styledTag } from 'src/utils/styled';

const LogoWoopenDefault = dynamic(() => import('src/assets/logos/logo-woopen-default.svg'), {
    ssr: false,
});

const StyledLogoWoopenDefault = styledComponent(LogoWoopenDefault)`
    position: absolute;
    width: 10.375rem;
    height: 7.75rem;
`;

const StyledCardContent = styledComponent(CardContent)`
    position: relative;
    padding: 1.5rem 0;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const StyledAvatar = styledComponent(Avatar)`
    margin-left: 0.07rem;
    font-size: 3rem;
`;

const StyledLink = styledComponent(Link)`
    text-decoration: none;
`;

const SecondaryTitle = styledComponent(Typography)`
    color: ${colours.black.O87};
    font-size: 1.05rem;
`;

const AddressLabel = styledComponent(Typography)`
    color: ${colours.black.O87};
`;

const AvatarCardWrapper = styledTag('div')<{
    $hasUnreadStory?: boolean;
}>`
    position: absolute;
    margin-top: -6rem;
    margin-bottom: 0.5rem;
    padding: 0.235rem;
    height: 7.7rem;
    border-radius: 0.5rem;
    background: ${({ $hasUnreadStory }) =>
        $hasUnreadStory ? 'linear-gradient(104.83deg, #132e48 -10.97%, #0072da 83.61%)' : String(colours.white.main)};
    box-shadow: 0rem 0.09rem 0rem 0.01rem ${colours.greyOpacity.O40};
    cursor: pointer;
`;

const linkButtonStyle: SxProps = {
    textDecoration: 'unset !important',
    '&:hover': {
        textDecoration: 'underline !important',
    },
};

type PagesJaunesProType = Pick<
    PagesJaunesPro,
    | 'name'
    | 'address'
    | 'description'
    | 'proId'
    | 'activityLabel'
    | 'averageRating'
    | 'numberOfRatings'
    | 'thumbnailPath'
    | 'imagePath'
    | 'images'
    | 'ratingsPageUrl'
    | 'canBeClaimedByCurrentUser'
    | 'pagesJaunesProType'
>;

interface Props {
    showRatingsAndFollowersCount?: boolean;
    pagesJaunesPro?: PagesJaunesProType;
    quickSignupCompany?: QuickSignupCompany;
    ctaComponent?: JSX.Element;
    company?: Company;
}

/**
 * Items that are common for Company and for PagesJaunesPro (and eventually for QuickSignupCompany)
 */
type CommonCompanyHeaderItems = {
    name?: string;
    cityName?: string;
    zipCode?: string;
    ratingsPageUrl?: string;
    averageRating?: number;
    numberOfRatings?: number;
    mainImage: Medium;
    showcase: Medium[];
    avatarImage?: Medium;
    hasUnreadStory: boolean;
    initials?: string;
    companyTypeOrActivityKey: string;
    activityLabel?: string;
};
const defaultWoopenImageMedium = { url: defaultWoopenImagePath } as Medium;
const getCommonCompanyHeaderItems = (
    companyData: CompanyProfileQuery | undefined,
    pagesJaunesPro: PagesJaunesPro | undefined,
    quickSignupCompany: QuickSignupCompany | undefined,
    getCompanyActivityToDisplay: ReturnType<typeof useCompanyActivityToDisplay>,
): CommonCompanyHeaderItems | undefined => {
    if (companyData)
        return {
            name: companyData.companyProfile.name,
            averageRating: Math.round(Number(companyData.companyProfile.averageRating) * 10) / 10,
            numberOfRatings: companyData.companyProfile.numberOfRatings,
            mainImage: companyData.companyProfile.coverImage ?? defaultWoopenImageMedium,
            showcase: companyData.companyProfile.showcase as Medium[],
            hasUnreadStory: companyData.companyProfile.hasUnreadStory,
            avatarImage: companyData.companyProfile.image as Medium,
            initials: companyData.companyProfile.initials,
            cityName: companyData.companyProfile.address?.city?.name ?? companyData.companyProfile.address?.cityName,
            zipCode: companyData.companyProfile.address?.zipCode,
            companyTypeOrActivityKey: getCompanyActivityToDisplay(companyData.companyProfile),
        } as CommonCompanyHeaderItems;

    if (pagesJaunesPro) {
        const address: PagesJaunesAddress | undefined =
            pagesJaunesPro.address !== null ? pagesJaunesPro.address : undefined;
        const cityName: string | undefined =
            address?.cityName !== null && address?.cityName !== undefined ? (address.cityName as string) : undefined;
        const zipCode: string | undefined =
            address?.zipCode !== null && address?.zipCode !== undefined ? (address.zipCode as string) : undefined;
        const showcase: Medium[] = pagesJaunesPro.images
            .map((image) => image.imagePath)
            .filter<string>((imagePath): imagePath is string => Boolean(imagePath))
            .map(
                (imagePath) =>
                    ({
                        url: getPagesJaunesImageUrl(imagePath),
                    }) as Medium,
            );

        return {
            name: pagesJaunesPro.name,
            averageRating: pagesJaunesPro.averageRating,
            numberOfRatings: pagesJaunesPro.numberOfRatings,
            ratingsPageUrl: pagesJaunesPro.ratingsPageUrl,
            mainImage: pagesJaunesPro.imagePath
                ? ({ url: getPagesJaunesImageUrl(pagesJaunesPro.imagePath) } as Medium)
                : defaultWoopenImageMedium,
            avatarImage: pagesJaunesPro.thumbnailPath
                ? ({ url: getPagesJaunesImageUrl(pagesJaunesPro.thumbnailPath) } as Medium)
                : undefined,
            showcase,
            hasUnreadStory: false,
            initials: '',
            cityName,
            zipCode,
            companyTypeOrActivityKey: pagesJaunesPro.activityLabel
                ? `common:activities.${pagesJaunesPro.activityLabel}`
                : 'common:company',
            activityLabel: pagesJaunesPro.activityLabel,
        } as CommonCompanyHeaderItems;
    }

    if (quickSignupCompany) {
        return {
            name: quickSignupCompany.name,
            cityName: quickSignupCompany.address?.city?.name ?? quickSignupCompany.address?.cityName ?? undefined,
            zipCode: quickSignupCompany.address?.zipCode ?? undefined,
            averageRating: undefined,
            numberOfRatings: undefined,
            mainImage: defaultWoopenImageMedium,
            showcase: [],
            avatarImage: undefined,
            hasUnreadStory: false,
            initials: quickSignupCompany.initials ?? undefined,
            companyTypeOrActivityKey: quickSignupCompany.activity?.labelKey
                ? `common:activities.${quickSignupCompany.activity.labelKey}`
                : 'common:company',
        };
    }

    return undefined;
};

const CompanyHeader: React.FC<Props> = ({
    showRatingsAndFollowersCount = true,
    pagesJaunesPro = undefined,
    quickSignupCompany = undefined,
    ctaComponent,
}) => {
    const getCompanyActivityToDisplay = useCompanyActivityToDisplay();
    const { t } = useTranslation(['profile', 'common', 'company']);
    const [openedStoriesOwner, setOpenedStoriesOwner] = useState<Owner | undefined>(undefined);
    const router = useAppRouter();
    const currentUser = useUser();
    const { width } = useWindowSize();
    const { props: coverPictureLightboxProps, open: openCoverPictureLightBoxModal } = useModalOpenState();
    const { props: avatarLightboxProps, open: openAvatarLightBoxModal } = useModalOpenState();
    const { props: followModalProps, open: openFollowModal } = useModalOpenState();

    const isPagesJaunesPro = pagesJaunesPro !== undefined;
    const isQuickSignupCompany = quickSignupCompany !== undefined;
    const isCompany = !isPagesJaunesPro && !isQuickSignupCompany;

    const companyId = isCompany ? (router.query.companyId as string) : undefined;

    const { data } = useCompanyProfileQuery({
        variables: {
            companyId: companyId as string,
        },
        skip: !isCompany || !companyId,
    });

    const { trackRedirectToPagesJaunesMerchantPageRatingSection } = useEvents();

    const handleClickPagesJaunesRatings = (e: React.MouseEvent) => {
        if (pagesJaunesPro) {
            trackRedirectToPagesJaunesMerchantPageRatingSection({
                pagesJaunesMerchantId: pagesJaunesPro.proId,
                userId: currentUser?.id,
            });
        }
    };

    const commonCompanyHeaderItems = getCommonCompanyHeaderItems(
        data,
        pagesJaunesPro,
        quickSignupCompany,
        getCompanyActivityToDisplay,
    );

    if (isCompany && !data) {
        return <Loader />;
    }

    const handleAvatarClick = (open: () => void) => {
        if (isCompany && commonCompanyHeaderItems?.hasUnreadStory)
            setOpenedStoriesOwner({ company: { id: data?.companyProfile.id } } as Owner);
        else if (commonCompanyHeaderItems?.avatarImage && !openedStoriesOwner) open();
    };

    const ratingsContent = (
        <Box display="flex" alignItems="center" data-target-id="companyHeader-ratings">
            <Box ml={1} mr={0.5} mt={-0.5}>
                <Icon size="1.25rem" name="Star" color={theme.palette.tertiary.main} />
            </Box>
            {commonCompanyHeaderItems?.numberOfRatings && (
                <Typography color="dark">
                    <strong>{commonCompanyHeaderItems.averageRating}</strong> (
                    {commonCompanyHeaderItems.numberOfRatings > 300 ? `300+` : commonCompanyHeaderItems.numberOfRatings}
                    )
                </Typography>
            )}
        </Box>
    );

    const ratingsBox = isPagesJaunesPro ? (
        <StyledLink
            onClick={handleClickPagesJaunesRatings}
            href={commonCompanyHeaderItems?.ratingsPageUrl as string}
            openInNewTab
        >
            {ratingsContent}
        </StyledLink>
    ) : (
        <StyledLink href={`${routePathCompany}/${companyId}?anchor=rating-section`}>{ratingsContent}</StyledLink>
    );

    return (
        <Card elevation={0}>
            <Box
                display="flex"
                width="100%"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                style={{ cursor: 'pointer' }}
            >
                <Image
                    data-target-id="cover-image"
                    alt="company-cover"
                    src={commonCompanyHeaderItems?.mainImage.url ?? defaultWoopenImagePath}
                    style={{
                        objectFit: 'cover',
                        aspectRatio: `${ratioCover}/1`,
                        width: '100%',
                        height: 'auto',
                    }}
                    width={width - paddingAroundCoverInPx}
                    height={(width - paddingAroundCoverInPx) / ratioCover}
                    onClick={() => commonCompanyHeaderItems?.mainImage.url && openCoverPictureLightBoxModal()}
                />
                {!commonCompanyHeaderItems?.mainImage.url && <StyledLogoWoopenDefault />}
            </Box>
            {coverPictureLightboxProps.open && (
                <Lightbox
                    {...coverPictureLightboxProps}
                    mainImageUrl={commonCompanyHeaderItems?.mainImage.url ?? defaultWoopenImagePath}
                    images={commonCompanyHeaderItems?.mainImage ? [commonCompanyHeaderItems.mainImage] : []}
                />
            )}
            <StyledCardContent>
                <AvatarCardWrapper
                    $hasUnreadStory={commonCompanyHeaderItems?.hasUnreadStory}
                    onClick={() => handleAvatarClick(openAvatarLightBoxModal)}
                >
                    <StyledAvatar
                        data-target="company-avatar"
                        data-target-id="company-avatar"
                        isCompany={Boolean(commonCompanyHeaderItems)}
                        alt={commonCompanyHeaderItems?.name}
                        image={commonCompanyHeaderItems?.avatarImage}
                        fallbackText={commonCompanyHeaderItems?.initials}
                        size={116}
                        badgeIcon={pagesJaunesPro ? 'PagesJaunes' : undefined}
                        badgeSize={pagesJaunesPro ? '2rem' : undefined}
                        badgeColor={pagesJaunesPro ? 'transparent' : undefined}
                        badgeBgColor={pagesJaunesPro ? colours.white.main : undefined}
                        badgeMargin={pagesJaunesPro ? '0 -1rem -1rem 0' : undefined}
                        badgeBorderColor={pagesJaunesPro ? colours.white.main : undefined}
                        badgeBorderRadius={pagesJaunesPro ? '0.25rem' : undefined}
                        badgeBorderWidth={pagesJaunesPro ? '0.125rem' : undefined}
                    />
                    {isCompany && openedStoriesOwner?.company?.id && (
                        <StoriesModal
                            openedOwner={openedStoriesOwner}
                            owners={[{ company: { id: data?.companyProfile.id } } as Owner]}
                            onClose={() => {
                                setOpenedStoriesOwner(undefined);
                            }}
                        />
                    )}
                </AvatarCardWrapper>
                {avatarLightboxProps.open && (
                    <AvatarLightBox
                        {...avatarLightboxProps}
                        image={commonCompanyHeaderItems?.avatarImage ?? null}
                        alt={commonCompanyHeaderItems?.name}
                        isOnCompany
                    />
                )}
                <Box
                    // if Pages Jaunes company, add a margin because of Pages Jaunes badge on avatar
                    mt={pagesJaunesPro ? '3rem' : '2rem'}
                    pl="1rem"
                    pr="1rem"
                    width="100%"
                    data-target-id="companyHeader-name"
                    display="flex"
                    justifyContent="center"
                >
                    <Typography
                        data-target="company-name"
                        data-target-id="company-name"
                        variant="h1"
                        color={colours.black.main}
                        noWrap
                    >
                        {commonCompanyHeaderItems?.name}
                    </Typography>
                </Box>
                {commonCompanyHeaderItems?.companyTypeOrActivityKey && (
                    <Box textAlign="center" maxWidth="30rem" my={1} data-target-id="companyHeader-activity">
                        <SecondaryTitle>
                            {i18n?.exists(commonCompanyHeaderItems.companyTypeOrActivityKey)
                                ? t(commonCompanyHeaderItems.companyTypeOrActivityKey)
                                : commonCompanyHeaderItems.activityLabel ??
                                  commonCompanyHeaderItems.companyTypeOrActivityKey}
                        </SecondaryTitle>
                    </Box>
                )}

                <Box
                    display="flex"
                    alignItems="center"
                    data-target-id="companyHeader-locationAndRatings"
                    mb={2}
                    mt={-1}
                >
                    {Boolean(commonCompanyHeaderItems?.cityName && commonCompanyHeaderItems.zipCode) && (
                        <>
                            <Box ml={-0.25} mr={0.5} mt={0.25}>
                                <Icon size="1.25rem" name="Map" />
                            </Box>
                            <AddressLabel color="dark" data-target-id="companyHeader-location">
                                {`${commonCompanyHeaderItems?.cityName}, ${commonCompanyHeaderItems?.zipCode}`}
                            </AddressLabel>
                        </>
                    )}
                    {!isCompany && showRatingsAndFollowersCount && ratingsBox}
                </Box>

                {data?.companyProfile.group?.id && <GroupLabel group={data.companyProfile.group as Company} />}

                {isCompany && showRatingsAndFollowersCount && (
                    <Box display="flex" alignItems="center" data-target-id="companyHeader-ratingsAndFollowers">
                        {ratingsBox}
                        <Box display="flex" alignItems="center" ml={2}>
                            <Button variant="link" sx={linkButtonStyle} onClick={openFollowModal}>
                                <Icon name="NetworkFilled" size="1.25rem" />
                                <Typography
                                    ml={0.8}
                                    color={colours.black.O87}
                                    dangerouslySetInnerHTML={{
                                        __html: t('profile:followersHighlight', {
                                            highlight: data?.companyProfile.numberOfFollowers,
                                            plurial: data && data.companyProfile.numberOfFollowers > 1 ? 's' : '',
                                        }),
                                    }}
                                />
                            </Button>
                        </Box>
                    </Box>
                )}
                {!isQuickSignupCompany && ctaComponent}
                {followModalProps.open && <FollowModal {...followModalProps} company={data?.companyProfile} />}
            </StyledCardContent>
        </Card>
    );
};

export default CompanyHeader;
